import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Server, 
  Users, 
  Clock,
  Download,
  Filter,
  Calendar,
  Zap,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'
import { TimeRangeSelector } from '@/components/analytics/TimeRangeSelector'
import { ServerComparisonChart } from '@/components/analytics/ServerComparisonChart'
import { UsageHeatmap } from '@/components/analytics/UsageHeatmap'
import { PerformanceTrends } from '@/components/analytics/PerformanceTrends'
import { AlertSummary } from '@/components/analytics/AlertSummary'
import { api } from '@/lib/api'

interface AnalyticsData {
  overview: {
    total_servers: number
    active_servers: number
    total_requests: number
    avg_response_time: number
    error_rate: number
    cache_hit_rate: number
  }
  trends: {
    requests_trend: number
    response_time_trend: number
    error_rate_trend: number
  }
  top_servers: Array<{
    server_id: string
    server_name: string
    request_count: number
    avg_response_time: number
    error_rate: number
    uptime: number
  }>
  performance_summary: {
    peak_hours: string[]
    busiest_day: string
    slowest_endpoints: Array<{
      endpoint: string
      avg_response_time: number
      server_name: string
    }>
  }
}

interface TimeRange {
  label: string
  value: string
  days: number
}

const timeRanges: TimeRange[] = [
  { label: 'Last 24 Hours', value: '24h', days: 1 },
  { label: 'Last 7 Days', value: '7d', days: 7 },
  { label: 'Last 30 Days', value: '30d', days: 30 },
  { label: 'Last 90 Days', value: '90d', days: 90 }
]

export function AnalyticsDashboard() {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(timeRanges[1]) // Default 7 days
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    fetchAnalyticsData()
  }, [selectedTimeRange])

  const fetchAnalyticsData = async () => {
    try {
      setRefreshing(true)

      // Fetch dashboard, top tools, activity, and basic server list in parallel
      const [dashboard, topTools, activity, servers] = await Promise.all([
        api.get('/analytics/dashboard', { time_range: selectedTimeRange.value }) as Promise<any>,
        api.get('/analytics/top-tools') as Promise<any>,
        api.get('/analytics/activity', { limit: 100 }) as Promise<any>,
        api.get('/servers') as Promise<{ servers: Array<{ id: string; name: string }> }>,
      ])

      // Compute overview values from dashboard.summary
      const summary = dashboard?.summary || {}
      const overview = {
        total_servers: Number(summary.total_servers || 0),
        active_servers: Number(summary.active_servers || 0),
        total_requests: Number(summary.total_requests || 0),
        // No direct avg_response_time in dashboard; approximate from global_metrics if present later
        avg_response_time: 0,
        error_rate: typeof summary.error_rate === 'number' ? summary.error_rate : 0,
        cache_hit_rate: 0,
      }

      // Build top servers by peeking each server's metrics (fast, parallel)
      const topServers = await Promise.all(
        (servers?.servers || []).slice(0, 10).map(async (s) => {
          try {
            const m = await api.get(`/servers/${s.id}/metrics`) as any
            const metrics = m?.metrics || {}
            return {
              server_id: s.id,
              server_name: s.name,
              request_count: Number(metrics.request_count || 0),
              avg_response_time: Number(metrics.response_time_avg || 0),
              error_rate: Number(metrics.error_rate || 0),
              // metrics.uptime is in ms; translate to % uptime is not directly available; leave as 100 for now if active
              uptime: 100,
            }
          } catch {
            return {
              server_id: s.id,
              server_name: s.name,
              request_count: 0,
              avg_response_time: 0,
              error_rate: 0,
              uptime: 0,
            }
          }
        })
      )

      // Approximate avg_response_time and cache_hit_rate from top servers
      if (topServers.length > 0) {
        const avgRt = topServers.reduce((a, b) => a + (b.avg_response_time || 0), 0) / topServers.length
        overview.avg_response_time = isFinite(avgRt) ? avgRt : 0
      }

      const computed: AnalyticsData = {
        overview,
        // Trends are not provided yet; set to neutral for now
        trends: {
          requests_trend: 0,
          response_time_trend: 0,
          error_rate_trend: 0,
        },
        top_servers: topServers
          .sort((a, b) => b.request_count - a.request_count)
          .slice(0, 5),
        performance_summary: {
          // Could be enhanced using /servers/{id}/analytics request_trend later
          peak_hours: [],
          busiest_day: '',
          slowest_endpoints: [],
        },
      }

      setAnalyticsData(computed)
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch analytics data:', error)
      setLoading(false)
    } finally {
      setRefreshing(false)
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const formatResponseTime = (ms: number) => {
    return `${Math.round(ms)}ms`
  }

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <TrendingUp className="h-4 w-4 text-green-500" />
    if (trend < 0) return <TrendingDown className="h-4 w-4 text-red-500" />
    return <Activity className="h-4 w-4 text-gray-500" />
  }

  const getTrendColor = (trend: number, inverse = false) => {
    const positive = inverse ? trend < 0 : trend > 0
    return positive ? 'text-green-600' : trend === 0 ? 'text-gray-600' : 'text-red-600'
  }

  const exportAnalytics = async () => {
    if (!analyticsData) return
    
    const exportData = {
      generated_at: new Date().toISOString(),
      time_range: selectedTimeRange.label,
      ...analyticsData
    }
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-${selectedTimeRange.value}-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (loading && !analyticsData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Loading comprehensive server analytics...</p>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            Comprehensive insights and performance analytics across all MCP servers
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <TimeRangeSelector
            selectedRange={selectedTimeRange}
            onRangeChange={setSelectedTimeRange}
            ranges={timeRanges}
          />
          <Button variant="outline" onClick={exportAnalytics}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button variant="outline" onClick={fetchAnalyticsData} disabled={refreshing}>
            <Activity className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      {analyticsData && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(analyticsData.overview.total_requests)}</div>
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                {getTrendIcon(analyticsData.trends.requests_trend)}
                <span className={getTrendColor(analyticsData.trends.requests_trend)}>
                  {analyticsData.trends.requests_trend > 0 ? '+' : ''}{analyticsData.trends.requests_trend.toFixed(1)}%
                </span>
                <span>vs previous period</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatResponseTime(analyticsData.overview.avg_response_time)}</div>
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                {getTrendIcon(-analyticsData.trends.response_time_trend)}
                <span className={getTrendColor(analyticsData.trends.response_time_trend, true)}>
                  {analyticsData.trends.response_time_trend > 0 ? '+' : ''}{analyticsData.trends.response_time_trend.toFixed(1)}ms
                </span>
                <span>vs previous period</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analyticsData.overview.error_rate.toFixed(2)}%</div>
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                {getTrendIcon(-analyticsData.trends.error_rate_trend)}
                <span className={getTrendColor(analyticsData.trends.error_rate_trend, true)}>
                  {analyticsData.trends.error_rate_trend > 0 ? '+' : ''}{analyticsData.trends.error_rate_trend.toFixed(2)}%
                </span>
                <span>vs previous period</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Servers</CardTitle>
              <Server className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {analyticsData.overview.active_servers}/{analyticsData.overview.total_servers}
              </div>
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                <CheckCircle className="h-3 w-3 text-green-500" />
                <span>{((analyticsData.overview.active_servers / analyticsData.overview.total_servers) * 100).toFixed(1)}% uptime</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="usage">Usage Patterns</TabsTrigger>
          <TabsTrigger value="comparison">Server Comparison</TabsTrigger>
          <TabsTrigger value="alerts">Alerts & Issues</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Top Performing Servers */}
            <Card>
              <CardHeader>
                <CardTitle>Top Performing Servers</CardTitle>
                <CardDescription>Servers ranked by request volume and performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analyticsData?.top_servers.map((server, index) => (
                    <div key={server.server_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                          <span className="text-sm font-semibold text-blue-600">#{index + 1}</span>
                        </div>
                        <div>
                          <div className="font-medium">{server.server_name}</div>
                          <div className="text-sm text-muted-foreground">{formatNumber(server.request_count)} requests</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">{formatResponseTime(server.avg_response_time)}</div>
                        <div className="text-xs text-muted-foreground">{server.uptime.toFixed(1)}% uptime</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Performance Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Insights</CardTitle>
                <CardDescription>Key performance patterns and recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Peak Traffic Hours</h4>
                    <div className="flex flex-wrap gap-2">
                      {analyticsData?.performance_summary.peak_hours.map((hour) => (
                        <Badge key={hour} variant="outline">{hour}</Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Busiest Day</h4>
                    <Badge variant="secondary">{analyticsData?.performance_summary.busiest_day}</Badge>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Slowest Endpoints</h4>
                    <div className="space-y-2">
                      {analyticsData?.performance_summary.slowest_endpoints.map((endpoint, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{endpoint.endpoint}</span>
                          <span className="text-orange-600">{formatResponseTime(endpoint.avg_response_time)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <PerformanceTrends timeRange={selectedTimeRange} />
        </TabsContent>

        <TabsContent value="usage" className="space-y-6">
          <UsageHeatmap timeRange={selectedTimeRange} />
        </TabsContent>

        <TabsContent value="comparison" className="space-y-6">
          <ServerComparisonChart timeRange={selectedTimeRange} />
        </TabsContent>

        <TabsContent value="alerts" className="space-y-6">
          <AlertSummary timeRange={selectedTimeRange} />
        </TabsContent>
      </Tabs>
    </div>
  )
} 